import { useMemo } from "react";
import { Task } from "@common/types/task";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import classNames from "classnames";
import { HStack } from "@design-system/HStack/HStack";
import { TasksEstimateTotals } from "./TasksEstimateTotals";
import { getSelectedTasksEstimateTotals } from "./TasksEstimateTotals.lib";
import styles from "./TasksPage.module.scss";

interface TasksBulkActionsFooterProps {
    show: boolean;
    onDeselectRows: () => void;
    onSetStatus: () => void;
    /** Omitted where tasks can't be bulk assigned (the organization page). */
    onAssignMember?: () => void;
    onOpenBulkActions: () => void;
    selectedCount: number;
    selectedTasks: Task[];
}

export const TasksBulkActionsFooter = ({
    show,
    onDeselectRows,
    onSetStatus,
    onAssignMember,
    onOpenBulkActions,
    selectedCount,
    selectedTasks,
}: TasksBulkActionsFooterProps) => {
    const estimateTotals = useMemo(
        () => getSelectedTasksEstimateTotals(selectedTasks),
        [selectedTasks],
    );

    return (
        <CommandIsland
            isOpen={show}
            sticky
            alignEnd
            className={classNames(
                commandIslandStyles.bulkActionsStickyFooter,
                styles.tasksBulkActionsCommandIsland,
            )}
        >
            <HStack gap={8}>
                {estimateTotals && (
                    <TasksEstimateTotals totals={estimateTotals} />
                )}
                <BulkSelectionControl
                    size="md"
                    selectedCount={selectedCount}
                    onClearSelection={onDeselectRows}
                />
            </HStack>
            <HStack gap={8}>
                <WithHotkey
                    key={`status-${selectedCount}`}
                    hotKey="s"
                    hotKeyOptions={{
                        description: `Set status`,
                        preventDefault: true,
                    }}
                    tooltip={`Set status for ${selectedCount} selected`}
                >
                    <Button variant="primary" size="md" onClick={onSetStatus}>
                        Set status
                    </Button>
                </WithHotkey>
                {onAssignMember && (
                    <WithHotkey
                        key={`assign-${selectedCount}`}
                        hotKey="a"
                        hotKeyOptions={{
                            description: `Assign member`,
                            preventDefault: true,
                        }}
                        tooltip={`Assign member to ${selectedCount} selected`}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onAssignMember}
                        >
                            Assign member
                        </Button>
                    </WithHotkey>
                )}
                <Tooltip content="Open bulk actions">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={onOpenBulkActions}
                        rightIcon={
                            <span data-slot-box="">
                                <Shortcut keys="mod+k" />
                            </span>
                        }
                    >
                        Actions
                    </Button>
                </Tooltip>
            </HStack>
        </CommandIsland>
    );
};
